import { useMemo, useState } from "react";

import { RoleGate } from "@/components/RoleGate";
import {
  useCancelarDevolucao,
  useConfirmarDevolucao,
  useCriarDevolucao,
  useDevolucoes,
  useLocais,
  useLotesEmEstoque,
} from "@/hooks/useApi";
import { useUser } from "@/hooks/useUser";
import type {
  Devolucao,
  MotivoDevolucao,
  StatusDevolucao,
  TipoDevolucao,
} from "@/types/api";

const STATUS_LABEL: Record<StatusDevolucao, string> = {
  aberta: "Em trânsito",
  concluida: "Concluída",
  cancelada: "Cancelada",
};
const STATUS_CLASS: Record<StatusDevolucao, string> = {
  aberta: "badge bg-amber-100 text-amber-800",
  concluida: "badge bg-green-100 text-green-800",
  cancelada: "badge bg-gray-100 text-gray-500 line-through",
};

const MOTIVO_LABEL: Record<MotivoDevolucao, string> = {
  paciente_desistiu: "Paciente desistiu",
  sobra: "Sobra",
  avaria: "Avaria",
  recall: "Recall",
  vencimento_proximo: "Vencimento próximo",
  outro: "Outro",
};

const TIPO_LABEL: Record<TipoDevolucao, string> = {
  enfermagem_para_farmacia: "Enfermagem → Farmácia",
  farmacia_para_fornecedor: "Farmácia → Fornecedor",
};

const MOTIVOS_POR_TIPO: Record<TipoDevolucao, MotivoDevolucao[]> = {
  enfermagem_para_farmacia: ["paciente_desistiu", "sobra", "vencimento_proximo", "outro"],
  farmacia_para_fornecedor: ["avaria", "recall", "vencimento_proximo", "outro"],
};

function fmtDate(s: string): string {
  return s.split("-").reverse().join("/");
}
function fmtDateTime(s: string): string {
  return new Date(s).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
}

export function DevolucoesPage() {
  return (
    <RoleGate allowed={["admin", "auditor", "compras", "farmaceutico", "enfermeira_chefe", "enfermagem"]}>
      <Inner />
    </RoleGate>
  );
}

function Inner() {
  const { data: user } = useUser();
  const { data: devolucoes, isLoading } = useDevolucoes();
  const [showNova, setShowNova] = useState(false);

  const podeCriar =
    user && ["admin", "compras", "farmaceutico", "enfermeira_chefe", "enfermagem"].includes(user.papel);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold">Devoluções</h2>
          <p className="text-sm text-ink-muted">
            Enfermagem → Farmácia (paciente desistiu, sobra) ou Farmácia → Fornecedor
            (avaria, recall, vencimento). Fluxo em 2 etapas: criar (saldo sai) → confirmar (chega no destino).
          </p>
        </div>
        {podeCriar ? (
          <button className="btn-primary" onClick={() => setShowNova((v) => !v)}>
            {showNova ? "Fechar" : "Nova devolução"}
          </button>
        ) : null}
      </div>

      {showNova ? <NovaDevolucao onDone={() => setShowNova(false)} /> : null}

      {isLoading ? <p className="text-ink-muted">Carregando…</p> : null}
      {devolucoes && devolucoes.length > 0 ? (
        <div className="space-y-3">
          {devolucoes.map((d) => <DevCard key={d.id} d={d} />)}
        </div>
      ) : devolucoes && devolucoes.length === 0 ? (
        <p className="text-sm text-ink-muted">Sem devoluções registradas.</p>
      ) : null}
    </div>
  );
}

function DevCard({ d }: { d: Devolucao }) {
  const { data: user } = useUser();
  const confirmar = useConfirmarDevolucao();
  const cancelar = useCancelarDevolucao();

  const podeConfirmar =
    user && d.status === "aberta" && (
      user.papel === "admin" ||
      (d.tipo === "enfermagem_para_farmacia" && user.papel === "farmaceutico") ||
      (d.tipo === "farmacia_para_fornecedor" && user.papel === "compras")
    );
  const podeCancelar =
    user && d.status === "aberta" &&
    ["admin", "farmaceutico", "enfermeira_chefe", "compras"].includes(user.papel);

  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <b className="font-mono text-sm">{d.numero}</b>
            <span className={STATUS_CLASS[d.status]}>{STATUS_LABEL[d.status]}</span>
            <span className="badge bg-gray-100 text-ink-muted">{TIPO_LABEL[d.tipo]}</span>
            <span className="badge bg-gray-100 text-ink-muted">{MOTIVO_LABEL[d.motivo]}</span>
          </div>
          <p className="text-sm text-ink-muted mt-1">
            <b>{d.origem_local_descricao}</b> →{" "}
            <b>{d.destino_local_descricao ?? d.destino_fornecedor_razao}</b>
            {" · criada por "}{d.criado_por_nome} em {fmtDateTime(d.created_at)}
          </p>
          {d.nf_devolucao_numero ? (
            <p className="text-xs text-ink-muted mt-0.5">NF dev. {d.nf_devolucao_numero}</p>
          ) : null}
          {d.confirmado_por_nome ? (
            <p className="text-xs text-ink-muted mt-0.5">Confirmada por {d.confirmado_por_nome}</p>
          ) : null}
          {d.observacao ? <p className="text-sm text-ink-muted mt-1">📝 {d.observacao}</p> : null}
        </div>
        <div className="flex gap-2">
          {podeConfirmar ? (
            <button className="btn-primary" disabled={confirmar.isPending} onClick={() => confirmar.mutate(d.id)}>
              {confirmar.isPending ? "Confirmando…" : "Confirmar"}
            </button>
          ) : null}
          {podeCancelar ? (
            <button className="btn-secondary" disabled={cancelar.isPending} onClick={() => cancelar.mutate(d.id)}>
              Cancelar
            </button>
          ) : null}
        </div>
      </div>
      {confirmar.error ? <p className="text-anarosa text-sm mt-2">{(confirmar.error as Error).message}</p> : null}
      <div className="mt-3 space-y-1.5">
        {d.itens.map((it) => (
          <div key={it.id} className="flex flex-wrap items-baseline justify-between gap-2 rounded bg-gray-50 px-3 py-1.5 text-sm">
            <span><code className="text-xs">{it.medicamento_codigo}</code> {it.medicamento_descricao}</span>
            <span className="text-xs text-ink-muted">lote <b>{it.numero_lote}</b> · qtd <b>{it.quantidade}</b></span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Form nova devolução ----------
interface ItemRow {
  key: string;
  lote_id: string;
  quantidade: string;
}

function NovaDevolucao({ onDone }: { onDone: () => void }) {
  const { data: user } = useUser();
  const { data: locais } = useLocais();
  const [tipo, setTipo] = useState<TipoDevolucao>("enfermagem_para_farmacia");
  const farmacias = useMemo(() => (locais ?? []).filter((l) => l.tipo === "farmacia"), [locais]);
  const enfermagens = useMemo(() => (locais ?? []).filter((l) => l.tipo === "enfermagem"), [locais]);

  // Origens permitidas conforme tipo
  const origensPossiveis = tipo === "enfermagem_para_farmacia" ? enfermagens : farmacias;
  const enfDoUsuario = user?.local_id && (user.papel === "enfermagem" || user.papel === "enfermeira_chefe");
  const origens = enfDoUsuario
    ? origensPossiveis.filter((l) => l.id === user.local_id)
    : origensPossiveis;

  const [origem, setOrigem] = useState("");
  const [destinoLocal, setDestinoLocal] = useState("");
  const [motivo, setMotivo] = useState<MotivoDevolucao>("paciente_desistiu");
  const [observacao, setObservacao] = useState("");
  const [nfDevNum, setNfDevNum] = useState("");
  const [itens, setItens] = useState<ItemRow[]>([
    { key: crypto.randomUUID(), lote_id: "", quantidade: "1" },
  ]);

  // Pega lotes em estoque do local de origem
  const lotesEstoque = useLotesEmEstoque(origem || null);

  const motivos = MOTIVOS_POR_TIPO[tipo];
  // Ajusta motivo se trocar tipo
  if (!motivos.includes(motivo)) setTimeout(() => setMotivo(motivos[0]), 0);

  const criar = useCriarDevolucao();

  function addItem() {
    setItens((a) => [...a, { key: crypto.randomUUID(), lote_id: "", quantidade: "1" }]);
  }
  function update(key: string, p: Partial<ItemRow>) {
    setItens((a) => a.map((r) => (r.key === key ? { ...r, ...p } : r)));
  }
  function remove(key: string) {
    setItens((a) => a.filter((r) => r.key !== key));
  }

  async function submit() {
    if (!origem) return;
    const body = {
      tipo,
      origem_local_id: origem,
      destino_local_id: tipo === "enfermagem_para_farmacia" ? destinoLocal : undefined,
      destino_fornecedor_id: undefined as string | undefined, // v1 sem UI de fornecedor; backend exigirá se tipo=fornecedor
      motivo,
      observacao: observacao || undefined,
      nf_devolucao_numero: tipo === "farmacia_para_fornecedor" && nfDevNum ? nfDevNum : undefined,
      itens: itens
        .filter((i) => i.lote_id && Number(i.quantidade) > 0)
        .map((i) => ({ lote_id: i.lote_id, quantidade: Number(i.quantidade) })),
    };
    try {
      await criar.mutateAsync(body);
      onDone();
    } catch {
      // exibido em criar.error
    }
  }

  return (
    <div className="card p-4 space-y-4">
      <h3 className="font-display text-base font-semibold">Nova devolução</h3>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Tipo</label>
          <select
            className="input mt-1"
            value={tipo}
            onChange={(e) => {
              setTipo(e.target.value as TipoDevolucao);
              setOrigem("");
              setDestinoLocal("");
            }}
          >
            <option value="enfermagem_para_farmacia">{TIPO_LABEL.enfermagem_para_farmacia}</option>
            <option value="farmacia_para_fornecedor">{TIPO_LABEL.farmacia_para_fornecedor}</option>
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Origem</label>
          <select className="input mt-1" value={origem} onChange={(e) => setOrigem(e.target.value)}>
            <option value="">— escolha —</option>
            {origens.map((l) => <option key={l.id} value={l.id}>{l.descricao}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Motivo</label>
          <select className="input mt-1" value={motivo} onChange={(e) => setMotivo(e.target.value as MotivoDevolucao)}>
            {motivos.map((m) => <option key={m} value={m}>{MOTIVO_LABEL[m]}</option>)}
          </select>
        </div>
      </div>

      {tipo === "enfermagem_para_farmacia" ? (
        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Farmácia destino</label>
          <select className="input mt-1" value={destinoLocal} onChange={(e) => setDestinoLocal(e.target.value)}>
            <option value="">— escolha —</option>
            {farmacias.map((l) => <option key={l.id} value={l.id}>{l.descricao}</option>)}
          </select>
        </div>
      ) : (
        <div className="space-y-2">
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-muted">NF devolução (opcional)</label>
            <input
              className="input mt-1"
              placeholder="Número da NF de devolução ao fornecedor"
              value={nfDevNum}
              onChange={(e) => setNfDevNum(e.target.value)}
            />
          </div>
          <p className="text-xs text-ink-muted">
            ⚠️ Selecionar destinatário fornecedor pela UI fica pra rodada futura — por
            enquanto Compras anexa o ID via API direta ou usa devolução interna em testes.
          </p>
        </div>
      )}

      <div>
        <label className="text-xs uppercase tracking-wider text-ink-muted">Itens (lotes em estoque na origem)</label>
        <div className="mt-1 space-y-2">
          {itens.map((row) => (
            <ItemRowEdit
              key={row.key}
              row={row}
              lotesDisponiveis={lotesEstoque.data ?? []}
              onChange={(p) => update(row.key, p)}
              onRemove={itens.length > 1 ? () => remove(row.key) : undefined}
            />
          ))}
          <button className="text-anarosa text-sm hover:underline" onClick={addItem}>
            + Adicionar lote
          </button>
          {origem && lotesEstoque.data && lotesEstoque.data.length === 0 ? (
            <p className="text-xs text-amber-700">Local sem lotes em estoque.</p>
          ) : null}
        </div>
      </div>

      <textarea
        className="input" rows={2} placeholder="Observação (opcional)"
        value={observacao} onChange={(e) => setObservacao(e.target.value)}
      />

      <div className="flex items-center gap-3">
        <button
          className="btn-primary"
          disabled={
            !origem || criar.isPending ||
            (tipo === "enfermagem_para_farmacia" && !destinoLocal) ||
            itens.every((i) => !i.lote_id)
          }
          onClick={submit}
        >
          {criar.isPending ? "Salvando…" : "Criar devolução"}
        </button>
        {criar.error ? <span className="text-anarosa text-sm">{(criar.error as Error).message}</span> : null}
      </div>
    </div>
  );
}

function ItemRowEdit({
  row, lotesDisponiveis, onChange, onRemove,
}: {
  row: ItemRow;
  lotesDisponiveis: { lote_id: string; numero_lote: string; medicamento_codigo: string; medicamento_descricao: string; data_validade: string; quantidade: number }[];
  onChange: (p: Partial<ItemRow>) => void;
  onRemove?: () => void;
}) {
  const lote = lotesDisponiveis.find((l) => l.lote_id === row.lote_id);
  return (
    <div className="rounded-md border border-gray-200 p-3">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_100px_auto]">
        <select className="input" value={row.lote_id} onChange={(e) => onChange({ lote_id: e.target.value })}>
          <option value="">— escolha lote —</option>
          {lotesDisponiveis.map((l) => (
            <option key={l.lote_id} value={l.lote_id}>
              {l.medicamento_codigo} {l.medicamento_descricao} · lote {l.numero_lote} · saldo {l.quantidade}
            </option>
          ))}
        </select>
        <input
          className="input" type="number" min={1}
          max={lote?.quantidade ?? undefined}
          value={row.quantidade}
          onChange={(e) => onChange({ quantidade: e.target.value })}
        />
        {onRemove ? (
          <button className="text-ink-muted text-sm hover:text-anarosa" onClick={onRemove}>Remover</button>
        ) : <div />}
      </div>
      {lote ? (
        <p className="mt-1.5 text-xs text-ink-muted">
          Validade: {fmtDate(lote.data_validade)} · saldo disponível: <b>{lote.quantidade}</b>
        </p>
      ) : null}
    </div>
  );
}
